import { Box, Flex, Text, Tooltip } from '@vimeo/bokeh';
import { Checkmark, CloseX } from '@vimeo/bokeh-icons';

interface ComparisonTableRowProps {
  featureName: string;
  tooltip?: string;
  currentValue: string | boolean;
  targetValue: string | boolean;
  isLastRow?: boolean;
}

/**
 * Desktop variant: renders 3 bare grid cells (feature title, current value, target value).
 * Must be placed inside a CSS grid with 3 columns.
 */
export function ComparisonTableRowDesktop({
  featureName,
  tooltip,
  currentValue,
  targetValue,
  isLastRow = false,
}: ComparisonTableRowProps) {
  return (
    <>
      <Flex gap="xs" alignItems="center" pl="md" pr="sm" py="sm">
        <Text variant="heading-sm" letterSpacing="-0.03em" flex="1">
          {featureName}
        </Text>
        {tooltip && (
          <Tooltip label={tooltip} placement="top" hasArrow maxW="260px">
            <Box flexShrink={0} cursor="pointer" color="text-secondary" display="inline-flex">
              <InfoIcon />
            </Box>
          </Tooltip>
        )}
      </Flex>

      <Flex
        alignItems="center"
        justifyContent="center"
        px="md"
        py="8px"
        pb={isLastRow ? 'md' : '8px'}
        opacity={0.7}
        borderLeftWidth="2px"
        borderRightWidth="2px"
        borderStyle="solid"
        borderColor="stroke"
        borderBottomLeftRadius={isLastRow ? '2xl' : '0'}
        borderBottomRightRadius={isLastRow ? '2xl' : '0'}
        borderBottomWidth={isLastRow ? '2px' : '0'}
      >
        <CellContent value={currentValue} />
      </Flex>

      <Flex
        alignItems="center"
        justifyContent="center"
        px="md"
        py="8px"
        pb={isLastRow ? 'md' : '8px'}
        bg="background"
        borderBottomLeftRadius={isLastRow ? '2xl' : '0'}
        borderBottomRightRadius={isLastRow ? '2xl' : '0'}
      >
        <CellContent value={targetValue} />
      </Flex>
    </>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M12 22a10 10 0 1 1 10-10 10 10 0 0 1-10 10zm0-18a8 8 0 1 0 8 8 8 8 0 0 0-8-8z" />
      <path d="M11 11h2v6h-2zm1-2a1 1 0 0 1-1-1 1.46 1.46 0 0 1 0-.2.63.63 0 0 1 .06-.18.77.77 0 0 1 .09-.18 1.48 1.48 0 0 1 .12-.15.92.92 0 0 1 .33-.21 1 1 0 0 1 1.09.21A1.05 1.05 0 0 1 13 8a.84.84 0 0 1-.08.38 1 1 0 0 1-.21.33l-.15.12-.18.09-.18.08z" />
    </svg>
  );
}

function CellContent({ value, size = 'md' }: { value: string | boolean; size?: 'sm' | 'md' }) {
  const iconSize = size === 'sm' ? 16 : 20;

  if (typeof value === 'boolean') {
    if (value) {
      return <Checkmark width={iconSize} height={iconSize} />;
    }
    return <CloseX width={iconSize} height={iconSize} color="#9CA3AF" opacity={0.6} />;
  }

  return (
    <Text fontSize={size === 'sm' ? 'body-sm' : 'body-lg'} textAlign="center" lineHeight="1.2">
      {value}
    </Text>
  );
}
